<template>
  <div class="space-y-6" data-test-id="balance-transaction-detail-content">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <LoadingSpinner :is-loading="true">
        <template #loading-text>Loading transaction details...</template>
      </LoadingSpinner>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="flex items-center justify-center py-8">
      <div class="flex flex-col place-items-center gap-2">
        <XMarkIcon class="size-8 text-red-500" />
        <h3
          class="text-lg font-medium mb-2 text-surface-900 dark:text-surface-0"
          data-test-id="transaction-not-found"
        >
          Transaction Not Found
        </h3>
        <p class="text-sm text-surface-600 dark:text-surface-300 mb-4">{{ error }}</p>
      </div>
    </div>

    <!-- Transaction Details -->
    <div v-else-if="transactionDetail" class="space-y-6">
      <!-- Balance Transaction Details Panel -->
      <SectionPanel
        :title="`Balance Transaction Details: ${transactionDetail.Id}`"
        test-id="balance-transaction-detail-panel"
      >
        <template #actions>
          <Button
            v-if="transactionDetail?.IsRetryable && canRetry"
            :label="retryButtonLabel"
            icon="pi pi-refresh"
            severity="warn"
            size="small"
            :loading="isRetrying"
            :disabled="isRetrying"
            @click="confirmRetry"
            data-test-id="retry-transaction-btn"
          />
          <Button
            label="View Shopify Raw JSON"
            icon="pi pi-code"
            variant="outlined"
            size="small"
            @click="showRawJsonDialog = true"
            data-test-id="view-raw-json-btn"
          />
        </template>
        <dl class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div class="flex justify-between gap-4">
            <dt class="text-surface-600 dark:text-surface-300 font-medium">Transaction ID:</dt>
            <dd class="font-mono font-semibold text-surface-900 dark:text-surface-0">
              {{ transactionDetail.Id }}
            </dd>
          </div>

          <div class="flex justify-between gap-4">
            <dt class="text-surface-600 dark:text-surface-300 font-medium">Type:</dt>
            <dd class="text-surface-900 dark:text-surface-0">
              {{ transactionDetail.Type }}
            </dd>
          </div>

          <div class="flex justify-between gap-4">
            <dt class="text-surface-600 dark:text-surface-300 font-medium">Source Type:</dt>
            <dd class="text-surface-900 dark:text-surface-0">
              {{ transactionDetail.SourceType }}
            </dd>
          </div>

          <div class="flex justify-between gap-4">
            <dt class="text-surface-600 dark:text-surface-300 font-medium">Date:</dt>
            <dd class="text-surface-900 dark:text-surface-0">
              {{ formatDateTimeForDisplay(processedAt) }}
            </dd>
          </div>

          <div class="flex justify-between gap-4">
            <dt class="text-surface-600 dark:text-surface-300 font-medium">Processing State:</dt>
            <dd data-test-id="balance-txn-detail-processing-state">
              <BadgeProcessingState
                v-if="transactionDetail.ProcessingState"
                :status="transactionDetail.ProcessingState"
              />
              <span v-else class="text-surface-400 dark:text-surface-500">—</span>
            </dd>
          </div>

          <div class="flex justify-between gap-4">
            <dt class="text-surface-600 dark:text-surface-300 font-medium">Processing State Reason:</dt>
            <dd
              class="text-surface-900 dark:text-surface-0"
              data-test-id="balance-txn-detail-processing-state-reason"
            >
              {{ transactionDetail.ProcessingStateReason ?? '—' }}
            </dd>
          </div>

          <div class="flex justify-between gap-4">
            <dt class="text-surface-600 dark:text-surface-300 font-medium">Amount:</dt>
            <dd class="font-semibold text-surface-900 dark:text-surface-0">
              {{ transactionDetail.Amount }} {{ transactionDetail.Currency }}
            </dd>
          </div>

          <div class="flex justify-between gap-4">
            <dt class="text-surface-600 dark:text-surface-300 font-medium">Fee Amount:</dt>
            <dd class="font-semibold text-red-600">
              -{{ transactionDetail.FeeAmount }}
              {{ transactionDetail.FeeCurrency }}
            </dd>
          </div>

          <div class="flex justify-between gap-4">
            <dt class="text-surface-600 dark:text-surface-300 font-medium">Net Amount:</dt>
            <dd class="font-semibold text-green-600">
              {{ transactionDetail.NetAmount }}
              {{ transactionDetail.NetCurrency }}
            </dd>
          </div>

          <div class="flex justify-between gap-4">
            <dt class="text-surface-600 dark:text-surface-300 font-medium">Currency:</dt>
            <dd class="text-surface-900 dark:text-surface-0">
              {{ transactionDetail.Currency }}
            </dd>
          </div>

          <div class="flex justify-between gap-4">
            <dt class="text-surface-600 dark:text-surface-300 font-medium">Fee Currency:</dt>
            <dd class="text-surface-900 dark:text-surface-0">
              {{ transactionDetail.FeeCurrency }}
            </dd>
          </div>

          <div class="flex justify-between gap-4">
            <dt class="text-surface-600 dark:text-surface-300 font-medium">Net Currency:</dt>
            <dd class="text-surface-900 dark:text-surface-0">
              {{ transactionDetail.NetCurrency }}
            </dd>
          </div>
        </dl>
      </SectionPanel>

      <!-- Engine Requests Section -->
      <SectionPanel title="Engine Requests" test-id="engine-requests-panel">
        <DataTable
          v-if="
            transactionDetail.EngineRequests &&
            transactionDetail.EngineRequests.length > 0
          "
          :value="transactionDetail.EngineRequests"
          class="p-datatable-sm"
          stripedRows
          responsiveLayout="scroll"
          data-test-id="engine-requests-table"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items "
          :paginator="true"
          :rows="10"
        >
          <Column
            field="OrderTransactionId"
            header="Order Transaction ID"
            style="min-width: 10rem"
          >
            <template #body="{ data }">
              <span class="flex items-center gap-2 font-mono">
                <CogIcon class="size-4 text-surface-400" />
                {{ data.OrderTransactionId ?? '-' }}
              </span>
            </template>
          </Column>

          <Column field="State" header="State" style="min-width: 8rem">
            <template #body="{ data }">
              <span
                v-if="data.State"
                :class="getStateClass(data.State)"
                class="px-2 py-1 rounded-full text-xs font-medium"
              >
                {{ data.State }}
              </span>
              <span v-else class="text-surface-400 dark:text-surface-500">-</span>
            </template>
          </Column>

          <Column
            field="StateReason"
            header="State Reason"
            style="min-width: 12rem"
          >
            <template #body="{ data }">
              <span
                :class="
                  data.StateReason
                    ? 'text-surface-900 dark:text-surface-0'
                    : 'text-surface-400 dark:text-surface-500'
                "
              >
                {{ data.StateReason ?? '-' }}
              </span>
            </template>
          </Column>

          <Column field="EngineId" header="Engine ID" style="min-width: 12rem">
            <template #body="{ data }">
              <a
                v-if="data.EngineId && data.EngineIdType !== 'REFUND'"
                :href="getEngineLink(data)"
                class="text-rch-black dark:text-surface-0 underline hover:text-surface-800 dark:hover:text-surface-200 font-mono text-xs"
                target="_blank"
                data-test-id="engine-link"
              >
                {{ data.EngineId }}
              </a>
              <span
                v-else-if="data.EngineId"
                class="font-mono text-xs text-surface-700 dark:text-surface-300"
                data-test-id="engine-id-text"
              >
                {{ data.EngineId }}
              </span>
              <span v-else class="text-surface-400 dark:text-surface-500">-</span>
            </template>
          </Column>

          <Column
            field="EngineIdType"
            header="Engine ID Type"
            style="min-width: 8rem"
          >
            <template #body="{ data }">
              <span
                :class="
                  data.EngineIdType
                    ? 'text-surface-900 dark:text-surface-0'
                    : 'text-surface-400 dark:text-surface-500'
                "
              >
                {{ data.EngineIdType ?? '-' }}
              </span>
            </template>
          </Column>
        </DataTable>

        <!-- No Engine Requests -->
        <div
          v-else
          class="text-center py-4 text-surface-500 dark:text-surface-400"
          data-test-id="no-engine-requests"
        >
          No engine requests associated with this transaction.
        </div>
      </SectionPanel>

      <!-- Raw JSON Dialog -->
      <Dialog
        v-model:visible="showRawJsonDialog"
        header="Shopify Raw JSON"
        :style="{ width: '95vw', maxWidth: '1600px' }"
        modal
        data-test-id="raw-json-dialog"
      >
        <!--
          Same 1600px cap the payouts drawer and the transaction-detail drawer
          use, so a payload tree gets the same line length wherever an operator
          reads it. At 800px the deeper Shopify nodes wrapped and the indent ate
          most of the width.

          The dialog body does the height bounding, and scrolls on both axes: a
          tree grows down as nodes expand and right as they nest.
        -->
        <div class="bg-surface-50 dark:bg-surface-950 rounded-lg p-4 overflow-auto max-h-[60vh]">
          <JsonViewer
            :value="transactionDetail?.RawJson ?? null"
            empty-message="No raw JSON was recorded for this balance transaction."
          />
        </div>
      </Dialog>

      <!-- Retry confirmation dialog -->
      <TextConfirmDialog
        :visible="showRetryDialog"
        :header="retryButtonLabel"
        required-text="confirm"
        confirm-label="Retry"
        confirm-icon="pi pi-refresh"
        severity="warn"
        :is-loading="isRetrying"
        data-test-id="confirm-retry-dialog"
        @update:visible="showRetryDialog = $event"
        @confirm="handleRetryConfirm"
        @cancel="showRetryDialog = false"
      >
        <p class="text-surface-900 dark:text-surface-0 font-medium mb-2">
          {{ retryDialogMessage }}
        </p>
      </TextConfirmDialog>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useFrontegg } from '@frontegg/vue'
import { useToast } from 'primevue/usetoast'
import TextConfirmDialog from '@/components/common/TextConfirmDialog.vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import { XMarkIcon, CogIcon } from '@heroicons/vue/24/outline'
import { successToast, errorToastWithDetails } from '@/util/toasts'
import { normalizeError } from '@/util/errorNormalizer'
import { formatDateTimeForDisplay } from '@/util/dateUtils'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import JsonViewer from '@/components/common/JsonViewer.vue'
import SectionPanel from '@/components/common/SectionPanel.vue'
import BadgeProcessingState from '@/components/common/badges/BadgeProcessingState.vue'
import { shopifyPayoutsEmbeddedApi } from '@/services/shopifyPayoutsEmbeddedService'
import { useShopifyPayoutsStore } from '@/stores/shopifyPayoutsStore'
import { usePerms } from '@/composables/usePerms'
import { PERMISSIONS } from '@/config/permissions'
import type {
  ShopifyBalanceTransactionSummary,
  ShopifyBalanceTransactionDetail,
  ShopifyEngineRequest,
} from '@/types/shopifyPayouts'

interface Props {
  merchantId: string
  storeId: string
  businessEntityId: string
  payoutId: number
  balanceTransaction?: ShopifyBalanceTransactionSummary | null
  transactionId?: number | null
}

const props = defineProps<Props>()

const toast = useToast()
const { authState } = useFrontegg()
const store = useShopifyPayoutsStore()
const { hasPerms } = usePerms()

const transactionDetail = ref<ShopifyBalanceTransactionDetail | null>(null)
const isLoading = ref(false)
const isRetrying = ref(false)
const error = ref<string | null>(null)
const showRawJsonDialog = ref(false)
const showRetryDialog = ref(false)
const lastLoadedTransactionId = ref<number | null>(null)

const canRetry = computed(() => hasPerms(PERMISSIONS.SHOPIFY_PAYMENTS_UPDATE))
const hasEngineRequests = computed(() => (transactionDetail.value?.EngineRequests?.length ?? 0) > 0)
const retryButtonLabel = computed(() => hasEngineRequests.value ? 'Retry Failed Engine Requests' : 'Reprocess Balance Transaction')
const retryDialogMessage = computed(() => hasEngineRequests.value
  ? 'Are you sure you want to retry the failed engine requests for this balance transaction? This will re-trigger payment processing.'
  : 'Are you sure you want to reprocess this balance transaction? This will reset it to pending and run it through the full processing flow.',
)

const engineUrlBase = import.meta.env.VITE_ENGINE_URL || ''

const processedAt = computed(() => {
  if (!transactionDetail.value?.RawJson) return ''
  const rawJson = transactionDetail.value.RawJson as Record<string, unknown>
  return (rawJson.transactionDate as string) || ''
})

function getStateClass(state: string): string {
  switch (state?.toUpperCase()) {
    case 'COMPLETED':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
    case 'PENDING':
    case 'PENDING_CONFIRMATION':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200'
    case 'FAILED':
    case 'ERROR':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200'
    default:
      return 'bg-surface-100 text-surface-800 dark:bg-surface-800 dark:text-surface-200'
  }
}

function getEngineLink(engineRequest: ShopifyEngineRequest): string | null {
  if (!engineRequest.EngineId || engineRequest.EngineIdType === 'REFUND') return null
  if (engineRequest.EngineIdType === 'ADJUSTMENT') {
    return `${engineUrlBase}new/transactions?adjustment=${encodeURIComponent(engineRequest.EngineId)}`
  }
  return `${engineUrlBase}new/transactions?txn=${encodeURIComponent(engineRequest.EngineId)}`
}

function confirmRetry() {
  showRetryDialog.value = true
}

function handleRetryConfirm() {
  showRetryDialog.value = false
  executeRetry()
}

async function executeRetry() {
  const wasAlreadyRetrying = isRetrying.value
  isRetrying.value = true
  if (wasAlreadyRetrying) return

  try {
    const txnId = transactionDetail.value?.Id
    if (!txnId) return

    const updatedDetail = await shopifyPayoutsEmbeddedApi.retryBalanceTransaction(
      authState,
      props.merchantId,
      props.storeId,
      props.businessEntityId,
      props.payoutId,
      txnId,
    )

    transactionDetail.value = updatedDetail
    store.lastLoadedBalanceTransactionsPayoutId = null
    successToast(toast, 'Retry Initiated', 'The retry has been initiated successfully.')
  } catch (err: unknown) {
    const apiError = normalizeError(err)
    errorToastWithDetails(toast, 'Retry Failed', apiError.toToastMessage(), apiError)
  } finally {
    isRetrying.value = false
  }
}

async function loadTransactionDetail() {
  const txnId = props.balanceTransaction?.Id ?? props.transactionId
  if (!txnId || !props.merchantId || !props.payoutId) {
    return
  }

  // Prevent duplicate calls for the same transaction
  if (isLoading.value || lastLoadedTransactionId.value === txnId) {
    return
  }

  isLoading.value = true
  error.value = null
  transactionDetail.value = null

  try {
    transactionDetail.value =
      await shopifyPayoutsEmbeddedApi.getBalanceTransactionDetail(
        authState,
        props.merchantId,
        props.storeId,
        props.businessEntityId,
        props.payoutId,
        txnId,
      )

    lastLoadedTransactionId.value = txnId
  } catch (err: unknown) {
    const apiError = normalizeError(err)
    error.value = apiError.toToastMessage()
    errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
    // Reset on error so we can retry
    lastLoadedTransactionId.value = null
  } finally {
    isLoading.value = false
  }
}

// Load transaction detail when balance transaction or transactionId changes
watch(
  [() => props.balanceTransaction, () => props.transactionId],
  ([newTransaction, newTransactionId], [oldTransaction, oldTransactionId]) => {
    const newTxnId = newTransaction?.Id ?? newTransactionId
    const oldTxnId = oldTransaction?.Id ?? oldTransactionId

    // Only load if transaction actually changed (different ID)
    if (newTxnId && newTxnId !== oldTxnId) {
      // Reset tracking when transaction changes
      lastLoadedTransactionId.value = null
      loadTransactionDetail()
    }
  },
  { immediate: true },
)
</script>
